import { forwardRef } from "react";

const VARIANTS = {
  elevated: "shadow-card dark:shadow-none",
  outlined: "border border-line",
};

const PADDINGS = {
  none: "",
  sm: "p-4",
  md: "p-6",
  lg: "p-8",
};

const cx = (...classes) => classes.filter(Boolean).join(" ");

/**
 * Card — the standard surface panel.
 *
 * @param {object} props
 * @param {"elevated"|"outlined"} [props.variant="elevated"]
 * @param {"none"|"sm"|"md"|"lg"} [props.padding="none"] p-0 / p-4 / p-6 / p-8.
 *   Defaults to none so callers that pass padding through `extra` keep working;
 *   new code should use `padding="md"` (standard) or `"sm"` (compact).
 * @param {React.ElementType} [props.as="div"] Element/component to render, e.g. "section", "button", `Link`.
 * @param {string} [props.extra] Extra classes (legacy name, same as className).
 * @param {string} [props.className] Extra classes.
 * @param {boolean} [props.default] Legacy flag; renders the elevated style.
 * Any other props (onClick, to, aria-*, data-*) are forwarded to the element.
 */
const Card = forwardRef(function Card(
  {
    variant = "elevated",
    padding = "none",
    as: Component = "div",
    extra,
    className,
    default: _legacyDefault,
    children,
    ...rest
  },
  ref
) {
  return (
    <Component
      ref={ref}
      className={cx(
        "relative flex flex-col rounded-card bg-surface bg-clip-border text-fg",
        VARIANTS[variant] || VARIANTS.elevated,
        PADDINGS[padding],
        extra,
        className
      )}
      {...rest}
    >
      {children}
    </Component>
  );
});

/**
 * CardHeader — title row for a Card: heading + optional description on the
 * left, actions right-aligned.
 *
 * @param {object} props
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.description]
 * @param {React.ReactNode} [props.actions] Buttons/controls rendered on the right.
 * @param {React.ElementType} [props.titleAs="h2"] Heading element for the title.
 * @param {string} [props.className]
 */
export function CardHeader({
  title,
  description,
  actions,
  titleAs: Title = "h2",
  className,
}) {
  return (
    <div
      className={cx(
        "flex flex-wrap items-start justify-between gap-x-4 gap-y-2",
        className
      )}
    >
      <div className="min-w-0 flex-1">
        <Title className="text-heading">{title}</Title>
        {description && (
          <p className="mt-1 text-sm text-fg-muted">{description}</p>
        )}
      </div>
      {actions && (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      )}
    </div>
  );
}

export default Card;
